/* istanbul ignore file */
import {
  createEventCallbackRedirect,
  EVENT_TIMEOUT_IN_MS,
  sendGAEvent,
} from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';
import { Product } from '@noths/polaris-dev-ts-types';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking';
import { PageType } from 'src/types';
import { isCategoryPage, isPartnerPage, isSearchPage } from 'src/utils';
import { getProductFieldObject } from 'src/utils/googleAnalytics';
import { onProductClick } from '../modules/actions';
import {
  ClickEcommerce,
  NAVIGATION_TILE_VISIBILITY_NAME,
  NavigationTileImpression,
  NavigationTileImpressionActionReturn,
  ProductClick,
} from './events';

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [onProductClick.type]: (state, _nextState, action, dispatch) => {
    dispatch(visibilityTrackingActions.sendProductImpressionBatch());

    const {
      payload: { productCode, productUrl, destinationUrl, shouldUseEventCallback },
    } = action as ReturnType<typeof onProductClick>;
    const {
      products: { category, items, partner, searchTerm },
      navigation: { pageType },
    } = state;

    const clickedProduct = Object.keys(items)
      .reduce((curr, key) => curr.concat(items[key]), [] as Product[])
      .find((prod) => prod.code === productCode)!;

    const fieldObject = getProductFieldObject(clickedProduct, state);

    const ecommerceClick: ClickEcommerce['click'] = {
      actionField: {
        list: '',
      },
      products: [fieldObject],
    };

    if (isCategoryPage(pageType)) {
      ecommerceClick.actionField.list = `${PageType.Category} - ${category!.name}`;
    } else if (isPartnerPage(pageType)) {
      ecommerceClick.actionField.list = `${PageType.Partner} - ${partner!.name}`;
    } else if (isSearchPage(pageType)) {
      ecommerceClick.actionField.list = `${PageType.Search} - ${searchTerm}`;
    }

    if (clickedProduct.isSponsored) {
      ecommerceClick.actionField.list += ' - sponsored';
    }

    sendGAEvent<ProductClick>({
      event: 'product_clicks',
      event_category: 'Product listings',
      event_action: 'Product Click',
      event_label: 'null',
      destination_URL: destinationUrl,
      ecommerce: {
        click: ecommerceClick,
      },
      ...(shouldUseEventCallback && {
        eventCallback: createEventCallbackRedirect(productUrl),
        eventTimeout: EVENT_TIMEOUT_IN_MS,
      }),
    });
  },
  [visibilityTrackingActions.getSectionVisibleActionType(NAVIGATION_TILE_VISIBILITY_NAME)]: (
    _,
    __,
    action,
  ) => {
    const { payload } = action as NavigationTileImpressionActionReturn;

    sendGAEvent<NavigationTileImpression>({
      event: 'custom_event',
      event_category: 'Category tile',
      event_action: 'Impression',
      event_label: 'Stocking fillers',
      destination_url:
        'https://www.notonthehighstreet.com/christmas/christmas-gifts?filter%5Bdelivery%5D%5B%5D=get_by_event',
      content_position: `${payload.position}`,
      content_type: 'category tile',
      item_id: 'stocking filler',
    });
  },
};
